'use client';

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IStore } from '@/src/types/stores';
import { storesApi } from '../services/stores.service';
import StoreCard from './store-card';
import { trackEvent } from '@/src/lib/amplitude';
import { AmplitudeEvents } from '@/src/lib/amplitude/events';
import useStoreInfo from '@/src/lib/state-stores/storeInfoStore';
import { useLocationStore } from '@/src/lib/state-stores/locationStore';
import InfiniteGrid from '@/src/components/InfiniteGrid';

type Props = {
  initalStores: IStore[] | undefined;
  q?: string;
  departmentIds?: string[] | string;
  departmentsIds?: string[] | string;
  orderType?: string;
  hasOffers?: string;
};

const InfintyStores = ({
  initalStores,
  departmentIds,
  departmentsIds,
  orderType,
  q,
  hasOffers,
}: Props) => {
  const { setBranchId } = useStoreInfo();
  const { locationDetails } = useLocationStore();
  const lat = locationDetails?.location?.latitude;
  const lng = locationDetails?.location?.longitude;

  const [currentStores, setCurrentStores] = useState<IStore[]>(initalStores || []);

  const depts = departmentIds ?? departmentsIds;

  const isFilterByLocalShipping = orderType === '5';

  const branchIds = useMemo(() => {
    return currentStores
      .map((item) => Number((item as any).branchId || item?.id))
      .filter((id) => Boolean(id) && !isNaN(id));
  }, [currentStores]);

  const { data: etaData } = useQuery({
    queryKey: ['branches-eta', branchIds.join(','), lat, lng],
    queryFn: async () => {
      if (!branchIds.length || !lat || !lng) return [];
      return await storesApi.getBranchesEta({
        branchIds,
        latitude: lat,
        longitude: lng,
      });
    },
    enabled: branchIds.length > 0 && Boolean(lat) && Boolean(lng),
  });

  const etaMap = useMemo(() => {
    const map: Record<number, string> = {};
    if (Array.isArray(etaData)) {
      etaData.forEach((item) => {
        if (item?.branchId && item?.deliveryTime) {
          map[item.branchId] = item.deliveryTime;
        }
      });
    }
    return map;
  }, [etaData]);

  const fetchStoresData = async (pageParam: number | string) => {
    const params = {
      page: pageParam.toString(),
      orderType,
      q,
      departmentIds: depts,
      hasOffers,
      latitude: lat ? String(lat) : undefined,
      longitude: lng ? String(lng) : undefined,
    };
    const response = await storesApi.getStores(params);
    return response as IStore[];
  };

  return (
    <article>
      <InfiniteGrid<IStore>
        queryKey={['stores', depts, q, orderType, hasOffers, lat, lng]}
        initialData={initalStores}
        fetchFn={fetchStoresData}
        pageSize={50}
        gridClassName="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 lg:gap-5 mt-8"
        emptyComponent={<div>Error fetching products</div>}
        onDataChange={setCurrentStores}
        renderItem={(item, index) => {
          const currentStoreId = Number(item?.store?.id || item?.id);
          const currentBranchId = Number((item as any).branchId || item?.id);
          const deliveryTime = etaMap[currentBranchId];

          return (
            <StoreCard
              key={index}
              storeContent={item}
              deliveryTime={deliveryTime}
              isFilterByLocalShipping={isFilterByLocalShipping}
              onStoreClicked={() => {
                trackEvent(AmplitudeEvents.store_clicked, {
                  shop_id: currentStoreId,
                  shop_name: item?.store?.englishName,
                });
                setBranchId(undefined);
              }}
            />
          );
        }}
      />
    </article>
  );
};

export default InfintyStores;
